import FindFolderJsonResponse from 'owa-service/lib/contract/FindFolderJsonResponse';
import FindConversationJsonResponse from 'owa-service/lib/contract/FindConversationJsonResponse';
import FindFolderResponseMessage from 'owa-service/lib/contract/FindFolderResponseMessage';
import SingleResponseMessage from 'owa-service/lib/contract/SingleResponseMessage';
import FindItemJsonResponse from 'owa-service/lib/contract/FindItemJsonResponse';
import FindItemResponseMessage from 'owa-service/lib/contract/FindItemResponseMessage';
import FindConversationResponseMessage from 'owa-service/lib/contract/FindConversationResponseMessage';
import { SessionData } from 'owa-service/lib/types/SessionData';
import ServerVersionInfo from 'owa-service/lib/contract/ServerVersionInfo';
import { addBottleneck } from 'owa-performance';
import * as Schema from 'owa-graph-schema';
import { InitialMailData } from 'hx-initial-mail-data';
import { mapFindFolderResponseToGql } from 'owa-folder-gql-mappers';
import { shouldShowFolder } from 'owa-folders-data-utils';
import { lazyTryGetConversationRowsFromResponse } from './lazyIndex';
import { trace } from 'owa-trace';

let findConversationResponse: FindConversationJsonResponse | undefined;
let conversationRows: Schema.ConversationRowConnection | undefined;
let findFolderResponseMessage: FindFolderJsonResponse | undefined;
let folderHierarchyResult: Schema.FolderHierarchyResult | undefined;
let findMailFolderItemResponse: FindItemJsonResponse | undefined;
let exchangeServerVersionInfo: ServerVersionInfo | undefined;
let listViewDataRequestAttemped: boolean;

type BottleneckReason =
    | 'njr' // no json response
    | 'nrm' // no response message
    | 'wf' // wrong folder
    | 'wi' // wrong index
    | 'ni' // no items
    | 'e' // error response message
    | 's'; // success

export function getExchangeServerVersionInfo(): ServerVersionInfo | undefined {
    return exchangeServerVersionInfo;
}

// This is only exposed for unit testing and should not be used otherwise
export function updateListViewDataRequestAttemped(value: boolean) {
    listViewDataRequestAttemped = value;
}

export function stashSessionDataAndInitialData(
    sessionData: SessionData,
    initialMailData: InitialMailData | null
) {
    // VSO: 97417 When hosted in native host, the session data responses are stashed
    // after the initial load of the list view. This causes a subsequent folder load to
    // pick up the now out-of-date / incorrect response.
    // While the root cause is investigated, as a work around
    // ignore delay initialization of list view session data.
    findConversationResponse = listViewDataRequestAttemped
        ? undefined
        : sessionData.findConversation;
    conversationRows = listViewDataRequestAttemped ? undefined : initialMailData?.conversationRows;
    findMailFolderItemResponse = listViewDataRequestAttemped
        ? undefined
        : sessionData.findMailFolderItem;
    exchangeServerVersionInfo = sessionData.findFolders?.Header?.ServerVersionInfo;
    findFolderResponseMessage = sessionData.findFolders;
    folderHierarchyResult = initialMailData?.folderHierarchy;
}

// todo VSO 93880: remove this once we fully roll out mon-conv-useGqlForFindConv
export function tryConsumeFindConversationResponse(
    folderId: string,
    startIndex: number
): FindConversationResponseMessage | null {
    const bottleneck = 'fcr';
    const responseMessage: FindConversationResponseMessage | null = extractSingleResponseMessage(
        findConversationResponse,
        r => r.Body,
        bottleneck
    );

    // Always clear out the stash when consuming findConversation response from session data regardless of the folderId.
    // even if we're not requesting inbox. We want to get a fresh response when user navigates to Inbox later
    findConversationResponse = undefined;
    conversationRows = undefined;
    listViewDataRequestAttemped = true;

    if (responseMessage) {
        let reason: BottleneckReason | undefined;
        if (folderId != responseMessage.FolderId?.Id) {
            reason = 'wf';
        } else if (!responseMessage.Conversations) {
            reason = 'ni';
        } else if (
            typeof responseMessage.IndexedOffset != 'number' ||
            startIndex !== responseMessage.Conversations.length - responseMessage.IndexedOffset
        ) {
            reason = 'wi';
        }
        addBottleneck(bottleneck, reason || 's');
        if (!reason) {
            return responseMessage;
        }
    }

    return null;
}

export async function tryConsumeConversationRows(
    folderId: string,
    startIndex: number
): Promise<Schema.ConversationRowConnection | null> {
    const bottleneck = 'fcr';

    let cachedRows = conversationRows;
    const cachedResponse = findConversationResponse;

    // Always clear out the stash when consuming findConversation response from session data regardless of the folderId.
    // even if we're not requesting inbox. We want to get a fresh response when user navigates to Inbox later
    findConversationResponse = undefined;
    conversationRows = undefined;
    listViewDataRequestAttemped = true;

    if (!cachedRows && cachedResponse) {
        cachedRows = await lazyTryGetConversationRowsFromResponse.importAndExecute(cachedResponse);
    }
    if (!cachedRows) {
        return null;
    }

    let reason: BottleneckReason | undefined;
    if (folderId != cachedRows.folderId) {
        reason = 'wf';
    } else if (startIndex !== 0) {
        reason = 'wi';
    }
    addBottleneck(bottleneck, reason || 's');

    return reason ? null : cachedRows;
}

export function tryConsumeFindMailFolderItemResponse(
    folderId: string,
    startIndex: number,
    inboxFolderId: string
): FindItemResponseMessage | null {
    const bottleneck = 'fir';
    const responseMessage: FindItemResponseMessage | null = extractSingleResponseMessage(
        findMailFolderItemResponse,
        r => r?.Body?.ResponseMessages?.Items?.[0],
        bottleneck
    );

    // Always clear out the stash when consuming findItem response from session data regardless of the folderId.
    // even if we're not requesting inbox. We want to get a fresh response when user navigates to Inbox later
    findMailFolderItemResponse = undefined;
    listViewDataRequestAttemped = true;

    if (responseMessage) {
        let reason: BottleneckReason | undefined;
        const rootFolder = responseMessage?.RootFolder;
        if (folderId != inboxFolderId) {
            reason = 'wf';
        } else if (!rootFolder?.Items) {
            reason = 'ni';
        } else if (
            typeof rootFolder.IndexedPagingOffset != 'number' ||
            startIndex !== rootFolder.Items.length - rootFolder.IndexedPagingOffset
        ) {
            reason = 'wi';
        }
        addBottleneck(bottleneck, reason || 's');
        if (!reason) {
            return responseMessage;
        }
    }

    return null;
}

/**
 * The folder hierarchy data shall be consumed during boot. After that the
 * data is cleared to avoid issues of this response getting accidentally consumed
 * for connected accounts which load only on demand.
 */
export function tryConsumeFolderHierarchyResult(): Schema.FolderHierarchyResult | undefined {
    const bottleneck = 'ffr';
    // Clear out the response after consuming it
    let result = folderHierarchyResult;
    const response = findFolderResponseMessage;
    folderHierarchyResult = undefined;
    findFolderResponseMessage = undefined;
    if (!result && response) {
        result = tryGetFolderHierarchyFromResponse(response);
    }
    if (result) {
        addBottleneck(bottleneck, 's');
    }
    return result;
}

function tryGetFolderHierarchyFromResponse(findFolderResponse: FindFolderJsonResponse) {
    const findFolderResponseMessage = extractFindFoldersResponseMessage(findFolderResponse);
    if (!findFolderResponseMessage) {
        return undefined;
    }

    try {
        return mapFindFolderResponseToGql(
            findFolderResponseMessage,
            'UserMailbox',
            [] /* foldersToShowFirst */,
            shouldShowFolder('junkemail'),
            shouldShowFolder('notes'),
            false /* shouldPopulateChildFolderIds */
        );
    } catch (error) {
        trace.warn('tryGetFolderHierarchyFromResponse: ' + error.message);
        return undefined;
    }
}

export function extractFindFoldersResponseMessage(
    findFolderResponse: FindFolderJsonResponse | undefined
): FindFolderResponseMessage | null {
    const bottleneck = 'ffr';
    return extractSingleResponseMessage(
        findFolderResponse,
        r => r?.Body?.ResponseMessages?.Items?.[0],
        bottleneck
    );
}

export function extractSingleResponseMessage<T>(
    jsonResponse: T,
    extract: (T) => SingleResponseMessage,
    bottleneck: string
) {
    let bottleneckReason: BottleneckReason = 'njr';
    if (jsonResponse) {
        let singleResponseMessage: SingleResponseMessage = extract(jsonResponse);
        if (!singleResponseMessage) {
            bottleneckReason = 'nrm';
        } else if (singleResponseMessage.ResponseClass == 'Error') {
            bottleneckReason = 'e';
            addBottleneck(bottleneck + '_c', singleResponseMessage.ResponseCode || 'un');
        } else {
            return singleResponseMessage;
        }
    }
    addBottleneck(bottleneck, bottleneckReason);
    return null;
}
